import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2,
  CheckCircle,
  XCircle,
  Shield,
  ExternalLink,
  CreditCard,
} from "lucide-react";

import { useAppServices } from "hook/services";
import { useUserInfo } from "context/user";

const StripeCallbackPage = () => {
  const { user } = useUserInfo();
  const navigate = useNavigate();
  const { search } = useLocation();
  const AppServices = useAppServices();

  const code = new URLSearchParams(search).get("code");
  const state = new URLSearchParams(search).get("state");

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [message, setMessage] = useState(
    "We are securely connecting your account with Stripe..."
  );

  // Function: Update agency in DB with Stripe credentials
  const updateCompany = async (creds: any) => {
    const payload = {
      _id: user?.companyId,
      stripe: creds,
    };

    const { response } = await AppServices.company.update({ payload });

    if (!response) {
      setStatus("error");
      setMessage("Something went wrong while saving your Stripe details.");
      return;
    }

    setStatus("success");
    setMessage("Your Stripe account has been successfully integrated!");
    setTimeout(() => {
      window.close();
    }, 2000);
  };

  // Function: Get access token using code from Stripe
  const getCreds = async () => {
    try {
      const { response } = await AppServices.stripe.integrate_company({
        query: `code=${code}`,
      });

      if (!response) throw new Error("Stripe integration failed.");
      await updateCompany(response.data);
    } catch (err) {
      console.error(err);
      setStatus("error");
      setMessage("Unable to complete Stripe connection. Please try again.");
    }
  };
  let pageLoad = true;
  useEffect(() => {
    if (pageLoad) {
      if (code) {
        pageLoad = false;
        getCreds();
      } else {
        setStatus("error");
        setMessage("Missing authorization code in redirect URL.");
      }
      pageLoad = false;
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-blue-900 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-10 max-w-lg w-full"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {status === "loading" && "Connecting to Stripe"}
            {status === "success" && "Stripe Connected Successfully!"}
            {status === "error" && "Connection Failed"}
          </h1>

          <p className="text-gray-600 dark:text-gray-400">{message}</p>
        </div>

        {/* Status Icons */}
        <div className="flex flex-col items-center justify-center space-y-4">
          {status === "loading" && (
            <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
          )}
          {status === "success" && (
            <CheckCircle className="w-12 h-12 text-green-500" />
          )}
          {status === "error" && (
            <>
              <XCircle className="w-12 h-12 text-red-500" />
              <button
                onClick={() => navigate(-2)}
                className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-xl hover:bg-blue-700 transition-all"
              >
                Try Again
              </button>
            </>
          )}
        </div>

        {/* Security Badge */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Shield className="w-4 h-4" />
            <span>Secured by Stripe</span>
            <ExternalLink className="w-3 h-3" />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default StripeCallbackPage;
